/* eslint-disable jira/restricted/fedramp-isolated-cloud */
import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { currentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';

import { getBoardDetailsResource } from '@jira/router-resources__classic-projects/src/getBoardDetailsResource.tsx';
import { getEditModelResource } from '@jira/router-resources__classic-projects/src/getEditModelResource.tsx';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
	shouldLoadUIFBoardResources,
} from '@jira/router-resources__classic-projects/src/getRapidboardResources.tsx';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { forgeBoardModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';

import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';

import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { nextBestTaskFeatureResource } from '@jira/router-resources__next-best-task/src/nextBestTaskFeatureResource.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';

import { jiraSpaResources } from '@jira/router-resources__spa-page-container/src/jiraSpaResources.tsx';
import {
	uifBoardResource,
	boardViewSettingsResource,
} from '@jira/router-resources__uif-board/src/uifBoardResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { classicSoftwareBoardEmbedRoute } from '@jira/router-routes__software-classic-board-routes/src/classicSoftwareBoardEmbedRoute.tsx';
import {
	UIFBoardSpa,
	RapidBoardSpaPage,
} from '@jira/router-routes__user-board-entries/src/ui/async.tsx';
import { createLayout } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { isFedRamp } from '@atlassian/atl-context';
import { fg } from '@atlassian/jira-feature-gating';
import type { Route } from '@atlassian/react-resource-router';
import { classicBoardCapability } from './common/constants.tsx';

/**
 * Ordinarily it would be inadvisable to evaluate something like this in
 * a static route, as this could lead to inconsistent behaviour between
 * the server and the browser.
 *
 * In this case it is necessary and safe because:
 * 1) There are two versions of the board that share the same route
 *    and we can only tell them apart at runtime. When the old version
 *    is eventually removed, this should be cleaned up.
 * 2) No requests for board data are made on the server, so even
 *    if the value is incorrect on the server it won't be utilised
 * */

export const classicSoftwareBoardEmbedRouteEntry: Route = createEntry(
	classicSoftwareBoardEmbedRoute,
	() => ({
		group: 'classic-software',
		ufoName: 'classic-board-embed',
		// eslint-disable-next-line no-restricted-syntax
		component: RapidBoardSpaPage,
		layout: createLayout({
			isChromeless: true,
		}),
		resources: [
			...getNavigationResources(),
			...getIssueViewResources(),
			// eslint-disable-next-line jira/router/no-project-context-resource
			projectContextResource,
			staticProjectSidebarNavigationResource,
			...getRapidboardResources(),
			getRapidBoardDataResource(),
			resourceWithCondition(shouldLoadUIFBoardResources, uifBoardResource),
			// Load the NBT resource if we opt-in UIF, not fedRamp is enabled
			resourceWithCondition(
				() =>
					shouldLoadUIFBoardResources() &&
					(fg('aem_rr_classic_board')
						? isFeatureEnabled('jira', 'jira-next-best-task')
						: !isFedRamp()),
				nextBestTaskFeatureResource,
			),
			boardViewSettingsResource,
			resourceWithCondition(() => fg('board_ssr_missing_element_fix'), currentUserResource),
			getEditModelResource(),
			getBoardDetailsResource(),
			...jiraSpaResources(),
			forgeBoardModuleResource,
		],
		afterPaint: [LazyIssueApp],
		forPaint: [UIFBoardSpa],
		meta: {
			capability: classicBoardCapability,
		},
		bifrost: {
			bifrostGroup: 'shell-only',
		},
		excludeFromWrmDeferredLoading: true,
		isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-boards'),
	}),
);
